import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { api } from '@/lib/api';

/**
 * qc.ts — the QC review queue, and the rule a QC decision must satisfy.
 *
 * ### Why the floor is MIRRORED from the contract rather than imported
 *
 * `QC_REASON_MIN` / `QC_REASON_MAX` / `QcReviewReason` live in
 * `server/src/contract/result.ts`, and importing them here would be the obvious
 * thing to do — the `@contract` alias is wired in both `tsconfig.json` and
 * `vite.config.ts`, and the schemas are plain Zod with no server imports.
 *
 * **It has been tried, and it breaks the container build.** The client image
 * copies `server/src/contract` without a `server/node_modules`, so `zod` cannot
 * resolve from those files; a local `vite build` succeeds only because a dev
 * machine has the server's dependencies installed. That is why
 * `tsconfig.build.json` excludes `src/test`, why `lib/api.ts` mirrors the role
 * and scope vocabularies instead of importing them, and why the item editor
 * mirrors the `Item` rules. PROGRESS records the CI failure.
 *
 * So the guard moves to where it costs nothing: **`test/qc-review.test.tsx`
 * imports `QC_REASON_MIN` and `QC_REASON_MAX` from `@contract/result` and fails
 * the build if these numbers stop matching**, and parses a reason this schema
 * accepts through the contract's own `QcReviewReason`. Tests are excluded from
 * the image's typecheck, so they may reach into the contract freely — that is
 * exactly the seam `questions.test.tsx` already uses.
 *
 * Duplicating the number is cheap. Duplicating it *silently* is what the test
 * prevents.
 */

/** `QC_REASON_MIN` in `contract/result.ts`. Asserted equal by the test. */
export const QC_REASON_MIN = 12;
/** `QC_REASON_MAX` in `contract/result.ts`. Asserted equal by the test. */
export const QC_REASON_MAX = 500;

/**
 * The confirm dialog's form, validated before anything is sent.
 *
 * `.trim()` before the length check, or twelve spaces passes — the contract
 * trims for the same reason. The messages are **string keys**, not English: the
 * console is bilingual and this text is rendered by `useT`, so a message written
 * here in English would be the one sentence in the dialog an Arabic reader could
 * not read. `{min}` / `{max}` are interpolated at render.
 */
export const QcDecisionForm = z.object({
  reason: z
    .string()
    .trim()
    .min(QC_REASON_MIN, 'qcReview.reason.tooShort')
    .max(QC_REASON_MAX, 'qcReview.reason.tooLong'),
});

export type QcDecisionForm = z.infer<typeof QcDecisionForm>;

/**
 * ONE query key for the queue, used by the screen AND by the sidebar count.
 *
 * The badge is not a second source of truth: it reads the same cache entry the
 * QC review screen does, so a release that invalidates `['qc-queue']` updates
 * both, and the two can never disagree about how much is held.
 */
export const QC_QUEUE_KEY = ['qc-queue'] as const;

/**
 * How often the queue re-asks. The same cadence Candidates and CandidateDetail
 * poll at — a held result is delivery state, and inventing a third interval for
 * it would make "how fresh is this number" a per-screen question.
 */
export const QC_QUEUE_POLL_MS = 30_000;

export function useQcQueue() {
  return useQuery({
    queryKey: QC_QUEUE_KEY,
    queryFn: api.qcQueue,
    refetchInterval: QC_QUEUE_POLL_MS,
  });
}

/**
 * How many results are held right now — 0 while it is loading or unreachable.
 *
 * Zero on failure is deliberate and is the only safe default for a NAV BADGE: a
 * number that appears because a request failed would be an invented
 * notification, which is the thing `lib/notifications.ts` deleted a sample feed
 * to avoid. The screen behind it reports the failure properly; a sidebar
 * ornament must not.
 */
export function useQcHeldCount(): number {
  const { data } = useQcQueue();
  return data?.items?.length ?? 0;
}
